/**
 * 📐 장표(slide) 편집 계약 — ★순수 모듈 SSOT★.
 *
 * 채팅 편집(story-chat-lane)과 직접수정(inspector/story-data-edit)이 ★같은 화이트리스트★를 봐야
 * 한다. 두 축이 갈리면 "채팅으로는 되는데 직접수정에선 안 되는 키"(혹은 그 반대)가 생기고, 그
 * 어긋남은 화면에서 재현 불가로 보고된다. 이 모듈은 어디에도 의존하지 않는(타입 제외) 순수 함수
 * 뭉치라 두 축이 함께 import 할 수 있다.
 *
 * ★현재 직접수정(story-data-edit)의 정의는 그대로 둔다★ — 그 축의 계약(연출 키 불변)은 UI 가 없는
 * 상태에서 일부러 좁혀 둔 것이고, 이번 채팅 축이 그걸 넓힌다. 대신 ★텍스트 필드 목록·배열 캡은
 * 같은 값★이어야 하므로 테스트가 두 정의의 동일성을 행동으로 단정한다(문자열 스캔 아님).
 */
import type { StorySlide } from "./slide-kind-contract";

/* ─────────────── 값 목록(렌더러 계약 미러) ─────────────── */

/**
 * 앵커 9방향 — `remotion/msb/layouts.tsx` 의 `Anchor` 유니온과 1:1.
 * ★`top`·`bottom` 은 이번에 신설★ — 렌더러 `_anchorFlex` 는 startsWith/endsWith 판정이라
 * 값만 늘리면 상단중앙·하단중앙이 코드 변경 없이 동작한다(테스트가 그 사실을 못 박는다).
 */
export const SLIDE_ANCHORS = [
  "center", "top", "bottom", "left", "right",
  "top-left", "top-right", "bottom-left", "bottom-right",
] as const;
export type SlideAnchor = (typeof SLIDE_ANCHORS)[number];

export const SLIDE_REVEAL_MODES = ["all", "sequential", "cascade"] as const;
/** motion.tsx `KeywordEmphasisId` 미러. */
export const SLIDE_KEYWORD_EMPHASES = ["underline_sweep", "swipe_highlight", "box_slider_highlight"] as const;
/** tokens.tsx `CARD(variant)` 미러. */
export const SLIDE_CARD_STYLES = ["filled", "outlined", "elevated", "ghost", "accent", "tinted", "ruled"] as const;

/** 연출 키(전 타입 공통 — `cols` 만 cardGrid 전용). */
export const SLIDE_LAYOUT_KEYS = ["anchor", "revealMode", "keyword", "keywordEmphasis", "cardStyle", "cols"] as const;
export type SlideLayoutKey = (typeof SLIDE_LAYOUT_KEYS)[number];

/**
 * 타입별 ★텍스트 필드★ 화이트리스트 — `story-data-edit.SLIDE_TEXT_FIELDS` 와 같은 값.
 * mediaFocus 는 `src` 제외(읽기 전용 — https 검증 통과분을 편집 입력으로 덮지 않는다).
 */
export const SLIDE_TEXT_FIELDS_BY_TYPE: Readonly<Record<string, readonly string[]>> = {
  fullText: ["title", "sub"],
  kineticTypo: ["title", "sub"], // 🔤🎞 모션 타이포 — fullText 와 같은 데이터 계약
  checkList: ["title", "items"],
  cardGrid: ["title", "cards"],
  statRow: ["title", "stats"],
  bigNumber: ["value", "label", "sub"],
  splitFocus: ["label", "body", "sub"],
  stepFlow: ["title", "steps"],
  comparePair: ["title", "bad", "good"],
  mediaFocus: ["caption"],
  // 🪧 포스터 — 글자 네 층은 고치되 `src`(그림)는 이미지 장표와 같은 이유로 읽기 전용이다.
  posterStack: ["headline", "badge", "compare", "punch"],
  // 🏗 [건설] 사고 사례 브리핑 3종 — 칸 이름 = 기획·연출 사실 추출 키(`slide-kind-contract` CONSTRUCTION_SLIDE_TYPES 머리말).
  incidentBrief: ["tag", "title", "when", "where", "industry", "kind"],
  preventionCard: ["tag", "title", "preventions"],
  tbmCard: ["tag", "title", "checks", "source"],
};

/** 배열 필드 개수 상한 — `story-data-edit.SLIDE_ARRAY_CAPS` 미러(레이아웃 가독성 계약). */
export const SLIDE_ARRAY_CAPS = { items: 6, steps: 5, cards: 6, stats: 4, body: 4, preventions: 4, checks: 4 } as const;

/** 글 목록 칸(문자열 배열) — 항목·단계 + 🏗 예방대책·확인 항목. 같은 규율(빈 줄 제거 · 상한)을 받는다. */
export const SLIDE_STRING_LIST_KEYS = ["items", "steps", "preventions", "checks"] as const;
type SlideStringListKey = (typeof SLIDE_STRING_LIST_KEYS)[number];
const isStringListKey = (key: string): key is SlideStringListKey => (SLIDE_STRING_LIST_KEYS as readonly string[]).includes(key);

/**
 * ★절대 바꾸지 않는 키★ — 여기 들어오면 무조건 drop 한다.
 *  · `type` = 아바타 프레젠터 지문(slideSceneSignature)의 재료라 바꾸면 승계 지문이 깨진다.
 *  · `src` = mediaFocus 원본(검증 통과분).
 *  · `slideFrom` = "자리의 출처" 마커(내용의 출처가 아니다 — 승계·재파생 규율이 이걸 본다).
 */
export const SLIDE_FROZEN_KEYS = ["type", "src", "slideFrom"] as const;

/** 폰트·색 요청은 테마 토큰 소유 — 씬 데이터에 자리 자체가 없다. */
const SLIDE_THEME_OWNED_KEYS = new Set([
  "fontSize", "font", "fontFamily", "fontWeight", "size", "color", "textColor", "bg", "background", "backgroundColor",
]);

/* ─────────────── 사람이 읽는 라벨(정직 고지 문구 재료) ─────────────── */

const KEY_LABEL: Record<string, string> = {
  anchor: "정렬", revealMode: "등장 방식", keyword: "강조 키워드", keywordEmphasis: "강조 효과",
  cardStyle: "카드 스타일", cols: "카드 열 수",
  title: "제목", sub: "보조 문구", label: "라벨", value: "수치", caption: "설명",
  items: "항목", steps: "단계", cards: "카드", stats: "수치", body: "본문",
  bad: "비교(부정 면)", good: "비교(긍정 면)",
  tag: "꼬리표", when: "일시", where: "장소", industry: "업종", kind: "재해 유형",
  preventions: "예방대책", checks: "확인 항목", source: "출처",
  // ★금지 키의 라벨은 "무엇을 하려 했는지"로 적는다★ — 고지 문구가 "장표 종류 변경(지원 안 함)"처럼
  //   읽혀야 유저가 무엇이 막혔는지 바로 안다(키 이름만 보이면 그게 곧 침묵이다).
  type: "장표 종류 변경", src: "장표 이미지 교체", slideFrom: "장표 출처 변경",
};
export const slideKeyLabel = (key: string): string => KEY_LABEL[key] ?? key;

/** 앵커 → 한국어 라벨(정직 발화용). */
export const SLIDE_ANCHOR_LABEL: Record<SlideAnchor, string> = {
  center: "가운데", top: "위쪽 가운데", bottom: "아래쪽 가운데",
  left: "왼쪽", right: "오른쪽",
  "top-left": "왼쪽 위", "top-right": "오른쪽 위",
  "bottom-left": "왼쪽 아래", "bottom-right": "오른쪽 아래",
};

/* ─────────────── 정규화(직접수정 축과 같은 규약) ─────────────── */

const asStr = (v: unknown): string => (typeof v === "string" ? v : v == null ? "" : String(v));

/**
 * 🕳 ★자리표시자 문자열★ — `…`·`...`·`-`·`·` 처럼 "여기에 값을 넣으라"는 표식만 남은 값.
 *
 * 예시 문구(힌트 칩)나 LLM 템플릿이 그대로 실려 오면 멀쩡한 제목이 `…` 로 ★덮인다★ — 화면에는
 * 글자가 남아 있으니 유저는 "고쳐졌다"고 읽고, 실제로는 내용이 사라진 것이다(빈 값 drop 과 같은
 * 계열의 실패인데 빈 문자열이 아니라서 종전 검사를 그대로 통과했다 · 적대 3R P3-4).
 */
const PLACEHOLDER_TEXT_RE = /^[…·\-–—.\s]*$/;
const isPlaceholderText = (v: string): boolean => PLACEHOLDER_TEXT_RE.test(v);
const asStrArr = (v: unknown, cap: number): { arr: string[]; over: number } => {
  if (!Array.isArray(v)) return { arr: [], over: 0 };
  const over = Math.max(0, v.length - cap);
  return { arr: v.slice(0, cap).map((x) => asStr(x).trim()).filter((x) => x.length > 0), over };
};

/**
 * ★빈 원소는 담지 않는다(적대 1R P1-2)★ — `cards: ["가","나"]`(문자열 배열)처럼 형태가 어긋난
 * 응답을 그대로 정규화하면 `title:""` 카드가 그 수만큼 실려 ★멀쩡한 카드가 빈 상자로 덮이고★
 * applied 로 보고된다(거짓 완료 + 데이터 파괴). items/steps 가 이미 쓰는 규율(빈 문자열 제거 →
 * 남은 게 0이면 drop)을 카드·수치에도 똑같이 적용한다. 정체성 필드(title / value·label)가 비면 그
 * 원소는 화면에 아무것도 못 그리므로 원소 자체를 버린다.
 */
function asCards(v: unknown): { arr: Array<{ badge?: string; title: string; body?: string[] }>; over: number; seen: number; bad: number } {
  if (!Array.isArray(v)) return { arr: [], over: 0, seen: 0, bad: 0 };
  const over = Math.max(0, v.length - SLIDE_ARRAY_CAPS.cards);
  const kept = v.slice(0, SLIDE_ARRAY_CAPS.cards);
  const arr = kept
    .map((it) => {
      const o = (it && typeof it === "object" ? it : {}) as Record<string, unknown>;
      const badge = asStr(o.badge).trim();
      const body = asStrArr(o.body, SLIDE_ARRAY_CAPS.body).arr;
      return { ...(badge ? { badge } : {}), title: asStr(o.title).trim(), ...(body.length ? { body } : {}) };
    })
    .filter((c) => c.title.length > 0);
  return { arr, over, seen: kept.length, bad: kept.length - arr.length };
}

function asStats(v: unknown): { arr: Array<{ value: string; label: string }>; over: number; seen: number; bad: number } {
  if (!Array.isArray(v)) return { arr: [], over: 0, seen: 0, bad: 0 };
  const over = Math.max(0, v.length - SLIDE_ARRAY_CAPS.stats);
  const kept = v.slice(0, SLIDE_ARRAY_CAPS.stats);
  const arr = kept
    .map((it) => {
      const o = (it && typeof it === "object" ? it : {}) as Record<string, unknown>;
      return { value: asStr(o.value).trim(), label: asStr(o.label).trim() };
    })
    // 수치는 값·라벨 둘 다 있어야 한 칸이 성립한다(한쪽만 있으면 빈 칸이 그려진다).
    .filter((s) => s.value.length > 0 && s.label.length > 0);
  return { arr, over, seen: kept.length, bad: kept.length - arr.length };
}

function asSide(v: unknown): { side: { title: string; body?: string[] }; over: number } {
  const o = (v && typeof v === "object" ? v : {}) as Record<string, unknown>;
  const b = asStrArr(o.body, SLIDE_ARRAY_CAPS.body);
  return { side: { title: asStr(o.title).trim(), ...(b.arr.length ? { body: b.arr } : {}) }, over: b.over };
}

/* ─────────────── applySlideSet ─────────────── */

export type SlideSetDrop = {
  /** 드롭한 키(원문 그대로 — 미지 키도 그대로 담는다). */
  key: string;
  /** 사용자에게 보여줄 사유(짧게). */
  reason: string;
};

export type ApplySlideSetResult = {
  /** 적용 결과 장표. ★변경이 없으면 입력 참조를 그대로 돌려준다★(호출부의 no-op 판정 재료). */
  slide: StorySlide;
  /** 실제로 값이 바뀐 키(순서 = set 순회 순서). */
  applied: string[];
  /** 반영하지 못한 항목. */
  dropped: SlideSetDrop[];
};

/**
 * EF 가 돌려준 `set`(변경할 키만)을 ★화이트리스트 강제★로 장표에 적용한다.
 *
 * ★EF 를 신뢰하지 않는다★ — 금지 키(type·src·slideFrom)·미지원 값·캡 초과분·빈 값은 전부 drop 하고
 * 사유를 남긴다. 호출부가 그 사유를 그대로 사용자에게 말한다(조용한 무시 금지 = 막다른 길 금지 헌법).
 */
export function applySlideSet(slide: StorySlide, set: Record<string, unknown> | null | undefined): ApplySlideSetResult {
  const applied: string[] = [];
  const dropped: SlideSetDrop[] = [];
  if (!slide || typeof slide.type !== "string" || !set || typeof set !== "object") {
    return { slide, applied, dropped };
  }
  // ★hasOwn 조회★ — slide.type 은 코드(JSON)에서 온 값이라 "__proto__"/"toString" 이면 프로토타입
  //   체인이 잡혀 화이트리스트가 뚫린다(직접수정 축의 Codex 1R P2 와 같은 방어).
  const textFields = Object.prototype.hasOwnProperty.call(SLIDE_TEXT_FIELDS_BY_TYPE, slide.type)
    ? SLIDE_TEXT_FIELDS_BY_TYPE[slide.type]
    : null;
  const content = (slide.content && typeof slide.content === "object" ? slide.content : {}) as Record<string, unknown>;
  const next: Record<string, unknown> = { ...content };

  const same = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);
  const put = (key: string, value: unknown): void => {
    if (same(next[key], value)) return; // 이미 같은 값 = 적용도 드롭도 아니다(무변화)
    next[key] = value;
    applied.push(key);
  };

  for (const key of Object.keys(set)) {
    if (!Object.prototype.hasOwnProperty.call(set, key)) continue;
    const raw = (set as Record<string, unknown>)[key];

    if ((SLIDE_FROZEN_KEYS as readonly string[]).includes(key)) {
      dropped.push({ key, reason: "지원 안 함" });
      continue;
    }
    if (SLIDE_THEME_OWNED_KEYS.has(key)) {
      dropped.push({ key, reason: "글자 크기·색·폰트는 테마가 소유" });
      continue;
    }
    if (!textFields) {
      dropped.push({ key, reason: "지원하지 않는 장표 종류" });
      continue;
    }

    // ── 연출 키
    if (key === "anchor") {
      const v = asStr(raw).trim();
      if (!(SLIDE_ANCHORS as readonly string[]).includes(v)) { dropped.push({ key, reason: "지원하지 않는 정렬 값" }); continue; }
      put(key, v);
      continue;
    }
    if (key === "revealMode") {
      const v = asStr(raw).trim();
      if (!(SLIDE_REVEAL_MODES as readonly string[]).includes(v)) { dropped.push({ key, reason: "지원하지 않는 등장 방식" }); continue; }
      put(key, v);
      continue;
    }
    if (key === "keywordEmphasis") {
      const v = asStr(raw).trim();
      if (!(SLIDE_KEYWORD_EMPHASES as readonly string[]).includes(v)) { dropped.push({ key, reason: "지원하지 않는 강조 효과" }); continue; }
      put(key, v);
      continue;
    }
    if (key === "cardStyle") {
      const v = asStr(raw).trim();
      if (!(SLIDE_CARD_STYLES as readonly string[]).includes(v)) { dropped.push({ key, reason: "지원하지 않는 카드 스타일" }); continue; }
      put(key, v);
      continue;
    }
    if (key === "cols") {
      if (slide.type !== "cardGrid") { dropped.push({ key, reason: "카드형 장표에서만 바꿀 수 있음" }); continue; }
      const n = typeof raw === "number" ? raw : Number(asStr(raw));
      if (n !== 2 && n !== 3) { dropped.push({ key, reason: "2 또는 3만 가능" }); continue; }
      put(key, n);
      continue;
    }
    if (key === "keyword") {
      const v = asStr(raw).trim();
      if (!v) { dropped.push({ key, reason: "빈 값" }); continue; }
      if (isPlaceholderText(v)) { dropped.push({ key, reason: "내용이 비어 있어요" }); continue; }
      put(key, v);
      continue;
    }

    // ── 텍스트 필드
    if (!textFields.includes(key)) {
      dropped.push({ key, reason: "이 장표에서 바꿀 수 없는 항목" });
      continue;
    }
    if (isStringListKey(key)) {
      const cap = SLIDE_ARRAY_CAPS[key];
      const { arr, over } = asStrArr(raw, cap);
      if (!Array.isArray(raw)) { dropped.push({ key, reason: "목록 형식이 아님" }); continue; }
      if (!arr.length) { dropped.push({ key, reason: "빈 목록" }); continue; }
      if (over > 0) dropped.push({ key, reason: `${(raw as unknown[]).length}개→${cap}개(최대 ${cap}개)` });
      put(key, arr);
      continue;
    }
    if (key === "cards" || key === "stats") {
      if (!Array.isArray(raw)) { dropped.push({ key, reason: "목록 형식이 아님" }); continue; }
      const cap = key === "cards" ? SLIDE_ARRAY_CAPS.cards : SLIDE_ARRAY_CAPS.stats;
      const r = key === "cards" ? asCards(raw) : asStats(raw);
      // ★형태가 어긋난 응답은 "빈 목록"이 아니라 "형식" 문제로 말한다★ — 원소는 왔는데 전부
      //   버려졌다면 EF 가 `cards:["가","나"]` 처럼 잘못된 모양을 보낸 것이고, 유저에게는 그게
      //   "왜 안 들어갔는지"의 정답이다(빈 목록이라고 말하면 유저는 자기 요청을 의심한다).
      if (!r.arr.length) {
        dropped.push({ key, reason: raw.length ? `${key === "cards" ? "카드" : "수치"} 형식이 맞지 않음` : "빈 목록" });
        continue;
      }
      /* ★부분 필터도 말한다(적대 2R P2-5)★ — 6장 중 2장이 형식 미달로 빠졌는데 "고쳤어요"만
       *   돌아가면 유저는 사라진 2장을 ★자기가 잘못 말해서★ 라고 읽거나 아예 눈치채지 못한다.
       *   캡 초과(over)는 "너무 많아서 잘림", 이건 "모양이 틀려서 빠짐" — 사유가 다르니 따로 센다. */
      if (r.bad > 0) dropped.push({ key, reason: `${r.seen}개 중 ${r.bad}개는 형식이 맞지 않아 빠졌어요` });
      if (r.over > 0) dropped.push({ key, reason: `${raw.length}개→${cap}개(최대 ${cap}개)` });
      put(key, r.arr);
      continue;
    }
    if (key === "bad" || key === "good") {
      if (!raw || typeof raw !== "object") { dropped.push({ key, reason: "형식이 맞지 않음" }); continue; }
      const { side, over } = asSide(raw);
      if (!side.title) { dropped.push({ key, reason: "제목이 비어 있음" }); continue; }
      if (over > 0) dropped.push({ key, reason: `본문 최대 ${SLIDE_ARRAY_CAPS.body}줄` });
      put(key, side);
      continue;
    }
    // 나머지 = 단문 필드
    {
      const v = asStr(raw).trim();
      if (!v) { dropped.push({ key, reason: "빈 값" }); continue; }
      // 🕳 자리표시자(`…`·`...`)가 멀쩡한 문구를 덮지 않게 — 값이 아니라 빈칸 표시다(3R P3-4).
      if (isPlaceholderText(v)) { dropped.push({ key, reason: "내용이 비어 있어요" }); continue; }
      put(key, v);
    }
  }

  if (!applied.length) return { slide, applied, dropped };
  return { slide: { ...slide, content: next }, applied, dropped };
}

/** 부분 적용 고지 1줄(비면 ""). */
export function slideDropNote(dropped: SlideSetDrop[] | undefined | null): string {
  if (!dropped?.length) return "";
  const seen = new Set<string>();
  const parts: string[] = [];
  for (const d of dropped) {
    const t = `${slideKeyLabel(d.key)}(${d.reason})`;
    if (seen.has(t)) continue;
    seen.add(t);
    parts.push(t);
  }
  return `\n\n📐 적용 못 한 항목: ${parts.join(", ")}`;
}

/* ─────────────── 연출 키 승계(D-ⓐ) ─────────────── */

/**
 * 📐 ★연출 키 승계★ — 파생 장표를 새 대본으로 재감지(`detectSlideForSceneRange`)하면 content 가
 * 통째로 새 객체가 되어 유저가 맞춰 둔 anchor·revealMode·keyword·keywordEmphasis·cardStyle·cols 가
 * 조용히 사라진다(파생기는 연출 키를 만들지 않는다). 문구만 갈아끼우고 연출은 그대로 둔다.
 * ★새 장표가 이미 그 키를 들고 있으면 덮지 않는다★(현재 파생기엔 없지만, 생기면 새 값이 진실).
 */
export function carryOverSlideLayoutKeys(oldSlide: StorySlide | null | undefined, fresh: StorySlide): StorySlide {
  const oldContent = (oldSlide?.content && typeof oldSlide.content === "object" ? oldSlide.content : null) as Record<string, unknown> | null;
  if (!oldContent) return fresh;
  const freshContent = (fresh.content && typeof fresh.content === "object" ? fresh.content : {}) as Record<string, unknown>;
  const carried: Record<string, unknown> = {};
  for (const k of SLIDE_LAYOUT_KEYS) {
    if (freshContent[k] !== undefined) continue;
    const v = oldContent[k];
    if (v === undefined || v === null || v === "") continue;
    // cols 는 cardGrid 전용 — 타입이 달라졌으면 데려가지 않는다(쓰레기 키 영속 방지).
    if (k === "cols" && fresh.type !== "cardGrid") continue;
    carried[k] = v;
  }
  if (!Object.keys(carried).length) return fresh;
  return { ...fresh, content: { ...freshContent, ...carried } };
}

/* ─────────────── 씬 ↔ 장표 좌표 ─────────────── */

type SlideBearingSeg = { slide?: { type?: unknown; content?: unknown } | null };

/**
 * 씬 구간 [start, end) 안에서 장표를 소유한 첫 세그 인덱스(없으면 -1).
 * ★앵커 고정이 아니라 구간 스캔★ — 명시 주입(blueprint)은 앵커에, slide-auto 파생은 img 앵커에
 * 얹히므로 두 축이 어긋난 데이터에서도 같은 자리를 읽고 되쓴다(story-data-edit.slideSegIndexIn 미러).
 */
export function slideSegIndexInRange(segments: readonly SlideBearingSeg[], start: number, end: number): number {
  for (let s = Math.max(0, start); s < end && s < segments.length; s++) {
    const sl = segments[s]?.slide;
    if (sl && typeof sl.type === "string" && sl.content && typeof sl.content === "object") return s;
  }
  return -1;
}

/** 씬 인덱스(0-based) → 장표 소유 세그 인덱스(없으면 -1). sceneStartSegs = planScenesFromSegments 축. */
export function slideSegIndexByScene(
  segments: readonly SlideBearingSeg[],
  sceneStartSegs: readonly number[],
): number[] {
  return sceneStartSegs.map((start, i) => {
    const end = i + 1 < sceneStartSegs.length ? sceneStartSegs[i + 1] : segments.length;
    return slideSegIndexInRange(segments, start, end);
  });
}

/** 요청(프론트→story-chat EF) `blueprintSlides` 한 항목 — 스펙 계약 그대로. */
export type BlueprintSlideCtx = {
  /** 1-based 씬 번호(@씬N 축과 동일) */
  scene: number;
  type: string;
  /** 현재 anchor(없으면 null = center) */
  anchor: string | null;
  cols?: 2 | 3;
  cardStyle?: string;
  revealMode?: string;
  keyword?: string;
  keywordEmphasis?: string;
  /** 타입별 텍스트 필드만(src 제외). */
  text: Record<string, unknown>;
};

/** 현재 장표 목록 → EF 컨텍스트(장표 없는 씬은 항목 자체가 없다). */
export function buildBlueprintSlideContext(
  segments: readonly (SlideBearingSeg & Record<string, unknown>)[],
  sceneStartSegs: readonly number[],
): BlueprintSlideCtx[] {
  const at = slideSegIndexByScene(segments, sceneStartSegs);
  const out: BlueprintSlideCtx[] = [];
  at.forEach((segIdx, sceneIdx) => {
    if (segIdx < 0) return;
    const slide = segments[segIdx]?.slide as StorySlide | undefined;
    if (!slide || typeof slide.type !== "string") return;
    const content = (slide.content ?? {}) as Record<string, unknown>;
    const fields = Object.prototype.hasOwnProperty.call(SLIDE_TEXT_FIELDS_BY_TYPE, slide.type)
      ? SLIDE_TEXT_FIELDS_BY_TYPE[slide.type]
      : [];
    const text: Record<string, unknown> = {};
    for (const f of fields) if (content[f] !== undefined) text[f] = content[f];
    const cols = content.cols === 2 || content.cols === 3 ? (content.cols as 2 | 3) : undefined;
    out.push({
      scene: sceneIdx + 1,
      type: slide.type,
      anchor: typeof content.anchor === "string" && content.anchor ? content.anchor : null,
      ...(cols ? { cols } : {}),
      ...(typeof content.cardStyle === "string" && content.cardStyle ? { cardStyle: content.cardStyle } : {}),
      ...(typeof content.revealMode === "string" && content.revealMode ? { revealMode: content.revealMode } : {}),
      ...(typeof content.keyword === "string" && content.keyword ? { keyword: content.keyword } : {}),
      ...(typeof content.keywordEmphasis === "string" && content.keywordEmphasis ? { keywordEmphasis: content.keywordEmphasis } : {}),
      text,
    });
  });
  return out;
}

/* ─────────────── 방향 어휘 → 앵커 ─────────────── */

const V_TOP = /(맨\s*위|위쪽|위로|윗쪽|윗|상단|상측|top)/i;
const V_BOTTOM = /(맨\s*아래|아래쪽|아래로|아랫|하단|하측|밑|bottom)/i;
const H_LEFT = /(왼쪽|왼편|좌측|좌상|좌하|left)/i;
const H_RIGHT = /(오른쪽|오른편|우측|우상|우하|right)/i;
const H_CENTER = /(가운데|한가운데|중앙|센터|center|middle)/i;
/** 조합 없이 "위/아래"만 쓰는 짧은 형태("위에 붙여") — 단독 명사도 방향으로 본다. */
const V_TOP_BARE = /(?:^|[\s,])위(?:[에로]|$|[\s,])/;
const V_BOTTOM_BARE = /(?:^|[\s,])아래(?:[에로]|$|[\s,])/;

/**
 * 방향 어휘 → 9방향 앵커. 매핑 실패면 null.
 * ★수직·수평을 따로 읽어 조합한다★ — "좌상단"·"왼쪽 위"·"위쪽 가운데" 같은 변형을 나열형 사전으로
 * 잡으려 하면 항상 빠지는 표현이 남는다. "가운데"는 수직 축이 함께 오면 그 축이 이긴다
 * (= "위쪽 가운데" = top = 상단중앙, 렌더 계약과 같은 의미).
 */
export function parseSlideAnchorWord(text: string): SlideAnchor | null {
  const t = (text ?? "").trim();
  if (!t) return null;
  const top = V_TOP.test(t) || V_TOP_BARE.test(t);
  const bottom = V_BOTTOM.test(t) || V_BOTTOM_BARE.test(t);
  const left = H_LEFT.test(t);
  const right = H_RIGHT.test(t);
  const center = H_CENTER.test(t);
  // 양쪽이 동시에 잡히면(모순) 판정 포기 — 짐작으로 옮기지 않는다.
  if (top && bottom) return null;
  if (left && right) return null;
  const v = top ? "top" : bottom ? "bottom" : null;
  const h = left ? "left" : right ? "right" : null;
  if (v && h) return `${v}-${h}` as SlideAnchor;
  if (v) return v as SlideAnchor;
  if (h) return h as SlideAnchor;
  if (center) return "center";
  return null;
}

// Direct Edit allows empty fields; provider applySlideSet intentionally remains stricter.
const slideStr = (v: unknown): string => (typeof v === "string" ? v : v == null ? "" : String(v));
const slideStrArr = (v: unknown, cap: number): string[] => (Array.isArray(v) ? v.slice(0, cap).map(slideStr) : []);

/** 카드/스탯/비교면 = 알려진 텍스트 키만 남긴 정규화(미지 키 유입 차단). */
function slideCards(v: unknown): Array<{ badge?: string; title: string; body?: string[] }> {
  if (!Array.isArray(v)) return [];
  return v.slice(0, SLIDE_ARRAY_CAPS.cards).map((it) => {
    const o = (it && typeof it === "object" ? it : {}) as Record<string, unknown>;
    const badge = slideStr(o.badge);
    const body = slideStrArr(o.body, SLIDE_ARRAY_CAPS.body);
    return { ...(badge ? { badge } : {}), title: slideStr(o.title), ...(body.length ? { body } : {}) };
  });
}
function slideStats(v: unknown): Array<{ value: string; label: string }> {
  if (!Array.isArray(v)) return [];
  return v.slice(0, SLIDE_ARRAY_CAPS.stats).map((it) => {
    const o = (it && typeof it === "object" ? it : {}) as Record<string, unknown>;
    return { value: slideStr(o.value), label: slideStr(o.label) };
  });
}
function slideSide(v: unknown): { title: string; body?: string[] } {
  const o = (v && typeof v === "object" ? v : {}) as Record<string, unknown>;
  const body = slideStrArr(o.body, SLIDE_ARRAY_CAPS.body);
  return { title: slideStr(o.title), ...(body.length ? { body } : {}) };
}

/** patch 한 항목을 필드 계약(layouts.tsx props)에 맞게 정규화. */
export function normalizeSlideField(key: string, value: unknown): unknown {
  switch (key) {
    case "items": return slideStrArr(value, SLIDE_ARRAY_CAPS.items);
    case "steps": return slideStrArr(value, SLIDE_ARRAY_CAPS.steps);
    case "preventions": return slideStrArr(value, SLIDE_ARRAY_CAPS.preventions);
    case "checks": return slideStrArr(value, SLIDE_ARRAY_CAPS.checks);
    case "cards": return slideCards(value);
    case "stats": return slideStats(value);
    case "bad":
    case "good": return slideSide(value);
    default: return slideStr(value);
  }
}
